import { createContext, useContext, useMemo, useRef } from "react";

type CacheEntry = {
	// biome-ignore lint/suspicious/noExplicitAny:
	items: any[];
	fetchedPages: Set<number>;
	hasMore: boolean;
	count: number;
	querySignature: string;
	scrollPosition: number;
};

type CacheContextType = {
	getCache: (endpoint: string) => CacheEntry | undefined;
	setCache: (endpoint: string, entry: CacheEntry) => void;
	clearCache: (endpoint: string) => void;
};

const InfiniteScrollCacheContext = createContext<CacheContextType | null>(null);

export function InfiniteScrollCacheProvider({
	children,
}: {
	children: React.ReactNode;
}) {
	const cache = useRef<Map<string, CacheEntry>>(new Map());

	const value = useMemo(
		() => ({
			getCache: (endpoint: string) => cache.current.get(endpoint),
			setCache: (endpoint: string, entry: CacheEntry) => {
				cache.current.set(endpoint, entry);
			},
			clearCache: (endpoint: string) => {
				cache.current.delete(endpoint);
			},
		}),
		[],
	);

	return (
		<InfiniteScrollCacheContext.Provider value={value}>
			{children}
		</InfiniteScrollCacheContext.Provider>
	);
}

export const useInfiniteScrollCache = () => {
	const context = useContext(InfiniteScrollCacheContext);
	if (!context) {
		throw new Error(
			"useInfiniteScrollCache must be used within InfiniteScrollCacheProvider",
		);
	}
	return context;
};
